import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Preloader from './Preloader';
import FullScreenMenu from './FullScreenMenu';
import { useAuth } from '@/providers/AuthProvider';

interface PreloaderWrapperProps {
  children: React.ReactNode;
  showPreloader?: boolean;
}

const PreloaderWrapper: React.FC<PreloaderWrapperProps> = ({ 
  children, 
  showPreloader = true 
}) => {
  const location = useLocation();
  const { isLoading: authLoading } = useAuth();
  const [isPreloaderComplete, setIsPreloaderComplete] = useState(!showPreloader);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullyReady, setIsFullyReady] = useState(!showPreloader);
  const [shouldRenderChildren, setShouldRenderChildren] = useState(!showPreloader);

  const handlePreloaderComplete = () => {
    setIsPreloaderComplete(true);
    // Graceful delay to ensure smooth transition: preloader out → content in
    setTimeout(() => {
      setShouldRenderChildren(true);
      // Use double requestAnimationFrame for optimal paint timing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsFullyReady(true);
        });
      });
    }, 100); // Increased for smoother perception
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setIsFullyReady(true);
  };

  return (
    <>
      {!isPreloaderComplete && (
        <Preloader 
          onComplete={handlePreloaderComplete}
          authLoading={authLoading}
        />
      )}
      
      {isMenuOpen && (
        <FullScreenMenu 
          isOpen={isMenuOpen} 
          onClose={handleMenuClose} 
        />
      )}
      
      {/* GPU-accelerated transition with proper render sequencing */}
      {shouldRenderChildren && (
        <div 
          className={`transition-opacity duration-500 ease-out ${isFullyReady ? 'opacity-100' : 'opacity-0'}`}
          style={{
            willChange: isFullyReady ? 'auto' : 'opacity, transform',
            transform: 'translateZ(0)', // GPU layer promotion
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {children}
        </div>
      )}
    </>
  );
};

export default PreloaderWrapper;
